import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_END_POINT } from "../../Utils/Constant";
import { FiRefreshCw, FiTrash2, FiCheck, FiClock, FiAlertCircle } from 'react-icons/fi';
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';

// Utility function to format phone numbers
const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    // Add a space after the country code (e.g., "+91 8587812001")
    return phone.replace(/^(\+\d{1,3})(\d{1,})$/, '$1 $2');
};

const QueryDetails = () => {
    const [queries, setQueries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('all');

    useEffect(() => {
        fetchQueries();
    }, []);
          
    const fetchQueries = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_END_POINT}query/all`);
            if (response.data.success) {
                setQueries(response.data.data);
            }
        } catch (error) {
            setError('Failed to fetch queries');
            console.error('Error fetching queries:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (queryId, newStatus) => {
        try {
            const response = await axios.patch(`${API_END_POINT}query/${queryId}/status`, { status: newStatus });
            if (response.data.success) {
                setQueries(queries.map(query => 
                    query._id === queryId ? { ...query, status: newStatus } : query
                ));
            }
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    const handleDelete = async (queryId) => {
        try {
            const response = await axios.delete(`${API_END_POINT}query/${queryId}`);
            if (response.data.success) {
                setQueries(queries.filter(query => query._id !== queryId));
            }
        } catch (error) {
            console.error('Error deleting query:', error);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'in-progress':
                return 'bg-blue-100 text-blue-800';
            case 'resolved':
                return 'bg-green-100 text-green-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending':
                return <FiClock className="w-4 h-4" />;
            case 'in-progress':
                return <FiRefreshCw className="w-4 h-4" />;
            case 'resolved':
                return <FiCheck className="w-4 h-4" />;
            default:
                return <FiAlertCircle className="w-4 h-4" />;
        }
    };

    const filteredQueries = selectedStatus === 'all' 
        ? queries 
        : queries.filter(query => query.status === selectedStatus);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-64">
                <div className="text-red-500 text-center mb-4">{error}</div>
                <button
                    onClick={fetchQueries}
                    className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/80 transition-colors"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <h2 className="text-2xl font-bold text-gray-800">Query Management</h2>
                <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
                    <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary"
                    >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="in-progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                    </select>
                    <button
                        onClick={fetchQueries}
                        className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/80 transition-colors flex items-center gap-2"
                    >
                        <FiRefreshCw className="w-4 h-4" />
                        Refresh
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <Table className="min-w-full divide-y divide-gray-200">
                        <Thead className="bg-gray-50">
                            <Tr>
                                <Th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</Th>
                                <Th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</Th>
                                <Th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</Th>
                                <Th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</Th>
                                <Th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</Th>
                                <Th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</Th>
                                <Th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody className="bg-white divide-y divide-gray-200">
                            {filteredQueries.map((query) => (
                                <Tr key={query._id} className="hover:bg-gray-50">
                                    <Td className="px-6 py-4 whitespace-nowrap">{query.name}</Td>
                                    <Td className="px-6 py-4 whitespace-nowrap">{query.email}</Td>
                                    <Td className="px-6 py-4 max-w-xs truncate">{query.message}</Td>
                                    <Td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                            <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(query.status)}`}>
                                                {getStatusIcon(query.status)}
                                                <span className="ml-1 capitalize">{query.status}</span>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    const newStatus = query.status === 'pending' ? 'in-progress' : 
                                                                    query.status === 'in-progress' ? 'resolved' : 'pending';
                                                    handleStatusUpdate(query._id, newStatus);
                                                }}
                                                className="text-secondary hover:text-secondary/80 transition-colors"
                                            >
                                                <FiRefreshCw className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </Td>
                                    <Td className="px-6 py-4 whitespace-nowrap">
                                        {new Date(query.createdAt).toLocaleDateString()}
                                    </Td>
                                    <Td className="px-6 py-4 whitespace-nowrap">+{formatPhoneNumber(query.phone)}</Td>
                                    <Td className="px-6 py-4 whitespace-nowrap">
                                        <button
                                            onClick={() => handleDelete(query._id)}
                                            className="text-red-600 hover:text-red-900 transition-colors"
                                        >
                                            <FiTrash2 className="w-5 h-5" />
                                        </button>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </div>
            </div>
        </div>
    );
};

export default QueryDetails;
